/*
 * Le Défi Chanvrery — moteur déterministe partagé (navigateur + serveur)
 * ---------------------------------------------------------------------
 * Le serveur fournit une graine. La chute des tickets ET des obstacles ne dépend
 * que de cette graine. Le navigateur envoie uniquement la trace des positions du
 * panier (60 par seconde) ; le serveur rejoue la partie et calcule lui-même
 * score, vies et victoire. Le score envoyé par le navigateur n'est jamais cru.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.DefiEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var VERSION = 3;
  var WORLD = { W: 360, H: 600 };
  var TICK_HZ = 60;
  var BASKET = { y: 528, halfW: 34, catchTop: -14, catchBottom: 16 };
  var ITEM = { halfW: 16, halfH: 18 };       // fleur (≈ ronde) — hitbox commune aux obstacles
  var MAX_STEP = 16;        // déplacement max du panier par tick (960 px/s), identique doigt / souris / clavier
  var KEY_STEP = 12;        // vitesse aux flèches et boutons (720 px/s)
  var INVULN_TICKS = 40;    // 0,67 s d'invulnérabilité après un choc

  function mulberry32(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  var DEFAULTS = {
    durationSec: 45, goal: 30, lives: 3,
    spawnStartSec: 0.58, spawnEndSec: 0.36,    // intervalle moyen entre deux chutes (début → fin)
    speedStart: 235, speedEnd: 480,            // vitesse de chute px/s (début → fin) — v3 : +30 % / +26 %
    obstacleStart: 0.34, obstacleEnd: 0.55     // part d'obstacles (début → fin)
  };
  function normalize(cfg) {
    var c = {}; for (var k in DEFAULTS) c[k] = cfg && cfg[k] != null ? Number(cfg[k]) : DEFAULTS[k];
    c.seed = (cfg && cfg.seed) >>> 0; return c;
  }

  function createSim(cfgIn) {
    var cfg = normalize(cfgIn);
    var rng = mulberry32(cfg.seed);
    var totalTicks = Math.round(cfg.durationSec * TICK_HZ);
    var s = {
      tick: 0, totalTicks: totalTicks, bx: WORLD.W / 2, items: [], nextId: 1, spawnIn: 24,
      score: 0, lives: cfg.lives, hits: 0, missedTickets: 0, spawned: 0, invuln: 0,
      done: false, won: false, endReason: null, events: [], lastX: undefined
    };
    function lerp(a, b, p) { return a + (b - a) * p; }
    function spawn() {
      var p = s.tick / totalTicks;
      var span = WORLD.W - 44;
      var x = 22 + Math.round(rng() * span);
      if (s.lastX !== undefined && Math.abs(x - s.lastX) < 80) x = 22 + ((x - 22 + Math.round(span / 2)) % span);
      s.lastX = x;
      var bad = rng() < lerp(cfg.obstacleStart, cfg.obstacleEnd, p);
      var vy = lerp(cfg.speedStart, cfg.speedEnd, p) * (0.85 + rng() * 0.3);
      var sway = !bad && rng() < 0.3 ? 6 + rng() * 12 : 0;
      s.items.push({ id: s.nextId++, bad: bad, x0: x, x: x, y: -30, vy: vy / TICK_HZ, sway: sway, phase: rng() * 6.283, rot: (rng() - 0.5) * 0.5, age: 0 });
      s.spawned++;
      var interval = lerp(cfg.spawnStartSec, cfg.spawnEndSec, p) * (0.75 + rng() * 0.5);
      s.spawnIn = Math.max(9, Math.round(interval * TICK_HZ));
    }
    function step(targetX) {
      if (s.done) return s.bx;
      var lo = BASKET.halfW, hi = WORLD.W - BASKET.halfW;
      var want = clamp(Math.round(targetX), lo, hi);
      s.bx = clamp(s.bx + clamp(want - s.bx, -MAX_STEP, MAX_STEP), lo, hi);
      return tickOnce();
    }
    function stepFixed(bx) { if (s.done) return s.bx; s.bx = bx; return tickOnce(); }
    function tickOnce() {
      s.tick++; s.spawnIn--; if (s.invuln > 0) s.invuln--;
      if (s.spawnIn <= 0) spawn();
      var keep = [], zt = BASKET.y + BASKET.catchTop, zb = BASKET.y + BASKET.catchBottom;
      for (var i = 0; i < s.items.length; i++) {
        var t = s.items[i], prevB = t.y + ITEM.halfH;
        t.age++; t.y += t.vy;
        t.x = t.sway ? t.x0 + Math.sin(t.phase + t.age / 22) * t.sway : t.x0;
        var b = t.y + ITEM.halfH;
        var reach = BASKET.halfW + ITEM.halfW * (t.bad ? 0.2 : 0.3);
        if (b >= zt && prevB <= zb && Math.abs(t.x - s.bx) <= reach) {
          if (t.bad) {
            if (s.invuln === 0) { s.lives--; s.hits++; s.invuln = INVULN_TICKS; s.events.push({ type: 'hit', x: t.x }); }
            else s.events.push({ type: 'shield', x: t.x });
          } else { s.score++; s.events.push({ type: 'catch', x: t.x }); }
          continue;
        }
        if (t.y - ITEM.halfH > WORLD.H) { if (!t.bad) s.missedTickets++; continue; }
        keep.push(t);
      }
      s.items = keep;
      if (s.lives <= 0) { s.done = true; s.won = false; s.endReason = 'vies'; }
      else if (s.score >= cfg.goal) { s.done = true; s.won = true; s.endReason = 'objectif'; }
      else if (s.tick >= totalTicks) { s.done = true; s.won = false; s.endReason = 'temps'; }
      return s.bx;
    }
    return { state: s, cfg: cfg, step: step, stepFixed: stepFixed };
  }

  /** Vérification serveur : graine + trace (+ horodatages serveur). */
  function verify(cfgIn, trace, timing) {
    var reasons = [], sim = createSim(cfgIn), st = sim.state;
    if (!Array.isArray(trace) || trace.length === 0) reasons.push('trace vide');
    else if (trace.length > st.totalTicks) reasons.push('trace plus longue que la partie');
    var prev = WORLD.W / 2, still = 0, maxStill = 0, dirChanges = 0, lastDir = 0;
    for (var i = 0; reasons.length === 0 && i < trace.length; i++) {
      var bx = trace[i];
      if (typeof bx !== 'number' || bx !== Math.round(bx)) { reasons.push('position invalide'); break; }
      if (bx < BASKET.halfW || bx > WORLD.W - BASKET.halfW) { reasons.push('position hors terrain'); break; }
      var d = bx - prev;
      if (Math.abs(d) > MAX_STEP) { reasons.push('vitesse impossible'); break; }
      var dir = d > 0 ? 1 : d < 0 ? -1 : 0; if (dir && lastDir && dir !== lastDir) dirChanges++; if (dir) lastDir = dir;
      still = d === 0 ? still + 1 : 0; if (still > maxStill) maxStill = still;
      prev = bx; sim.stepFixed(bx);
      if (st.done && i < trace.length - 1) { reasons.push('trace après la fin de partie'); break; }
    }
    if (reasons.length === 0 && !st.done) reasons.push('partie incomplète');
    if (timing && timing.startedAtMs != null && timing.submittedAtMs != null) {
      var playedMs = (trace.length / TICK_HZ) * 1000, wallMs = timing.submittedAtMs - timing.startedAtMs;
      if (wallMs < playedMs * 0.92) reasons.push('soumission trop rapide');
      if (wallMs > playedMs + (timing.maxPauseMs || 180000)) reasons.push('interruption trop longue');
    }
    var valid = reasons.length === 0;
    return { valid: valid, won: valid && st.won, score: st.score, lives: Math.max(0, st.lives), hits: st.hits, endReason: st.endReason,
      ticks: Array.isArray(trace) ? trace.length : 0, stats: { maxStillTicks: maxStill, dirChanges: dirChanges, spawned: st.spawned }, reasons: reasons, engine: VERSION };
  }

  return { VERSION: VERSION, WORLD: WORLD, TICK_HZ: TICK_HZ, BASKET: BASKET, ITEM: ITEM, MAX_STEP: MAX_STEP, KEY_STEP: KEY_STEP, DEFAULTS: DEFAULTS,
    createSim: createSim, verify: verify, mulberry32: mulberry32 };
});
